(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const publish = (name, detail = {}) => {
    const payload = { ...detail, page: 'homepage' };
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish(name, payload);
    }
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
  };

  const installCartResultBridge = () => {
    const cart = window.NomSiteCart;
    if (!cart?.addFormEncoded || cart.__nomHomeResultBridge) return;
    cart.__nomHomeResultBridge = true;
    const addFormEncoded = cart.addFormEncoded.bind(cart);
    const claimedForms = new WeakSet();

    cart.addFormEncoded = (body) => {
      const params = new URLSearchParams(body);
      const target = [...document.querySelectorAll('[data-nom-product-form][data-nom-cart-add-pending="true"]')]
        .find((form) => {
          if (claimedForms.has(form)) return false;
          const formData = new FormData(form);
          const sameVariant = String(formData.get('id') || '') === String(params.get('id') || '');
          const samePlan = String(formData.get('selling_plan') || '') === String(params.get('selling_plan') || '');
          return sameVariant && samePlan;
        });
      if (target) claimedForms.add(target);

      return addFormEncoded(body)
        .then((result) => {
          if (target) document.dispatchEvent(new CustomEvent('nom:cart:add:success', { detail: { form: target } }));
          return result;
        })
        .catch((error) => {
          if (target) document.dispatchEvent(new CustomEvent('nom:cart:add:error', { detail: { form: target, error } }));
          throw error;
        })
        .finally(() => {
          if (target) claimedForms.delete(target);
        });
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installCartResultBridge, { once: true });
  } else {
    installCartResultBridge();
  }

  class NomProductPurchase extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.controller = new AbortController();
      this.form = this.querySelector('[data-nom-product-form]');
      if (!this.form) return;

      this.plan = this.querySelector('[data-nom-selling-plan]');
      this.frequency = this.querySelector('[data-nom-frequency]');
      this.button = this.querySelector('[data-add-to-cart]');
      this.buttonText = this.querySelector('[data-add-to-cart-text]');
      this.status = this.querySelector('[data-nom-cart-status]');
      this.defaultButtonText = this.buttonText?.textContent.trim() || 'Add to cart';
      this.wasPending = false;
      this.addSucceeded = false;

      this.addEventListener('change', this.onChange, { signal: this.controller.signal });
      this.form.addEventListener('formdata', this.onFormData, { signal: this.controller.signal });
      document.addEventListener('nom:cart:add:success', this.onCartAdded, { signal: this.controller.signal });
      document.addEventListener('nom:cart:add:error', this.onCartError, { signal: this.controller.signal });

      this.pendingObserver = new MutationObserver(this.onPendingChange);
      this.pendingObserver.observe(this.form, { attributes: true, attributeFilter: ['data-nom-cart-add-pending'] });
      this.syncPurchaseState(false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.pendingObserver?.disconnect();
      this.initialised = false;
    }

    selectedType = () => this.querySelector('[data-nom-purchase-type]:checked')?.value || 'one_time';

    syncPurchaseState = (announce) => {
      const purchaseType = this.selectedType();
      const subscription = purchaseType === 'subscription';

      this.querySelectorAll('[data-purchase-card]').forEach((card) => {
        card.classList.toggle('is-selected', card.dataset.purchaseCard === purchaseType);
      });

      if (this.plan) this.plan.disabled = !subscription;
      if (this.frequency) {
        this.frequency.hidden = !subscription;
        this.frequency.classList.toggle('is-collapsed', !subscription);
      }

      if (announce) {
        publish('nom_home_purchase_type_select', {
          product_handle: this.dataset.productHandle,
          section: this.dataset.sectionName,
          purchase_type: purchaseType,
          selling_plan_id: subscription ? this.plan?.value || null : null
        });
      }
    };

    onChange = (event) => {
      if (event.target.matches('[data-nom-purchase-type]')) {
        this.syncPurchaseState(true);
      }

      if (event.target.matches('[data-nom-selling-plan]')) {
        const option = event.target.selectedOptions[0];
        const price = this.querySelector('[data-nom-plan-price]');
        if (price && option?.dataset.planPrice) price.textContent = option.dataset.planPrice;
        publish('nom_home_purchase_type_select', {
          product_handle: this.dataset.productHandle,
          section: this.dataset.sectionName,
          purchase_type: 'subscription',
          selling_plan_id: event.target.value
        });
      }
    };

    onFormData = (event) => {
      [...event.formData.keys()].forEach((key) => {
        if (key.startsWith('purchase_type_')) event.formData.delete(key);
      });
      if (this.selectedType() !== 'subscription') event.formData.delete('selling_plan');
    };

    onCartAdded = (event) => {
      if (event.detail?.form !== this.form) return;
      this.addSucceeded = true;
      if (this.status) {
        this.status.textContent = `${this.dataset.productTitle || 'Item'} added to cart.`;
        this.status.classList.add('is-success');
      }
    };

    onCartError = (event) => {
      if (event.detail?.form !== this.form) return;
      window.NomSiteCart?.close?.();
      if (this.status) {
        this.status.textContent = 'We could not add that just now. Please try again.';
        this.status.classList.remove('is-success');
      }
    };

    onPendingChange = () => {
      const pending = this.form.dataset.nomCartAddPending === 'true';

      if (pending) {
        this.wasPending = true;
        this.addSucceeded = false;
        if (this.button) {
          this.button.disabled = true;
          this.button.setAttribute('aria-busy', 'true');
        }
        if (this.buttonText) this.buttonText.textContent = 'Adding…';
        if (this.status) {
          this.status.textContent = '';
          this.status.classList.remove('is-success');
        }
        return;
      }

      if (!this.wasPending) return;
      if (this.button) {
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }
      if (this.buttonText) this.buttonText.textContent = this.defaultButtonText;
      if (!this.addSucceeded && this.status) {
        this.status.textContent = 'We could not add that just now. Please try again.';
        this.status.classList.remove('is-success');
      }
      this.wasPending = false;
    };
  }

  if (!customElements.get('nom-product-purchase')) {
    customElements.define('nom-product-purchase', NomProductPurchase);
  }

  const initialiseReveals = (root = document) => {
    const items = [...root.querySelectorAll('.nom-reveal:not([data-nom-reveal-ready])')];
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.nomRevealReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    items.forEach((item, index) => {
      item.dataset.nomRevealReady = 'true';
      item.style.transitionDelay = `${Math.min(index % 3, 2) * 60}ms`;
      observer.observe(item);
    });
  };

  const initialiseReviewCarousels = (root = document) => {
    root.querySelectorAll('.nom-home-reviews:not([data-nom-carousel-ready])').forEach((section) => {
      const track = section.querySelector('[data-review-track]');
      const previous = section.querySelector('[data-review-prev]');
      const next = section.querySelector('[data-review-next]');
      const dots = [...section.querySelectorAll('[data-review-dot]')];
      if (!track || !previous || !next) return;

      section.dataset.nomCarouselReady = 'true';
      const originalCards = [...track.querySelectorAll('.nom-review-card')];
      const originalCount = originalCards.length;
      if (!originalCount) return;
      originalCards.forEach((card, index) => {
        card.dataset.reviewOriginalIndex = String(index);
      });

      const step = () => {
        const card = track.querySelector('.nom-review-card');
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return (card?.getBoundingClientRect().width || track.clientWidth) + gap;
      };

      const currentIndex = () => Math.max(0, Math.round(track.scrollLeft / step()));

      const currentReviewIndex = () => {
        const cards = [...track.querySelectorAll('.nom-review-card')];
        return Number(cards[currentIndex()]?.dataset.reviewOriginalIndex || 0);
      };

      const jumpToIndex = (index) => {
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = Math.max(0, index) * step();
        track.getBoundingClientRect();
        track.style.removeProperty('scroll-behavior');
      };

      const scrollToIndex = (index) => {
        track.scrollTo({ left: Math.max(0, index) * step(), behavior: 'smooth' });
      };

      const rebuildLoopClones = (preservePosition = true) => {
        const activeReview = preservePosition ? currentReviewIndex() : 0;
        track.querySelectorAll('[data-review-clone]').forEach((clone) => clone.remove());
        const cloneCount = Math.min(originalCount, Math.ceil(track.clientWidth / step()) + 1);
        originalCards.slice(0, cloneCount).forEach((card) => {
          const clone = card.cloneNode(true);
          clone.dataset.reviewClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('button, a, input, select, textarea').forEach((control) => {
            control.tabIndex = -1;
            control.hidden = true;
          });
          track.append(clone);
        });
        const targetIndex = originalCards.findIndex((card) => Number(card.dataset.reviewOriginalIndex) === activeReview);
        jumpToIndex(Math.max(0, targetIndex));
      };

      const syncControls = () => {
        const activeReview = currentReviewIndex();
        dots.forEach((dot) => {
          dot.hidden = false;
          if (Number(dot.dataset.reviewIndex) === activeReview) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const syncExpanders = () => {
        section.querySelectorAll('[data-review-expand]').forEach((button) => {
          const card = button.closest('.nom-review-card');
          const copy = card?.querySelector('[data-review-copy]');
          if (!card || !copy || card.hasAttribute('data-review-clone') || card.classList.contains('is-expanded')) return;
          button.hidden = copy.scrollHeight <= copy.clientHeight + 2;
        });
      };

      section.querySelectorAll('[data-review-expand]').forEach((button) => {
        button.addEventListener('click', () => {
          const card = button.closest('.nom-review-card');
          if (!card) return;
          const expanded = card.classList.toggle('is-expanded');
          button.setAttribute('aria-expanded', String(expanded));
          button.textContent = expanded ? 'Show less' : 'Read more';
        });
      });

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let autoplayTimer;
      let inView = !('IntersectionObserver' in window);
      let paused = false;

      const stopAutoplay = () => {
        window.clearInterval(autoplayTimer);
        autoplayTimer = undefined;
      };

      const advance = (force = false) => {
        if (!force && (!inView || paused || document.hidden)) return;
        const index = currentIndex();
        if (index >= originalCount) {
          jumpToIndex(index - originalCount);
          requestAnimationFrame(() => scrollToIndex(index - originalCount + 1));
          return;
        }
        scrollToIndex(index + 1);
      };

      const startAutoplay = () => {
        stopAutoplay();
        if (reduceMotion || !inView || paused || track.scrollWidth <= track.clientWidth + 2) return;
        autoplayTimer = window.setInterval(advance, 6500);
      };

      const restartAutoplay = () => {
        stopAutoplay();
        startAutoplay();
      };

      previous.addEventListener('click', () => {
        const index = currentIndex();
        if (index <= 0) {
          jumpToIndex(originalCount);
          requestAnimationFrame(() => scrollToIndex(originalCount - 1));
        } else {
          scrollToIndex(index - 1);
        }
        publish('nom_home_review_scroll', { direction: 'previous' });
        restartAutoplay();
      });

      next.addEventListener('click', () => {
        advance(true);
        publish('nom_home_review_scroll', { direction: 'next' });
        restartAutoplay();
      });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const target = Number(dot.dataset.reviewIndex) || 0;
          const targetIndex = originalCards.findIndex((card) => Number(card.dataset.reviewOriginalIndex) === target);
          scrollToIndex(Math.max(0, targetIndex));
          restartAutoplay();
        });
      });
      section.addEventListener('mouseenter', () => {
        paused = true;
        stopAutoplay();
      });
      section.addEventListener('mouseleave', () => {
        if (section.contains(document.activeElement)) return;
        paused = false;
        startAutoplay();
      });
      section.addEventListener('focusin', () => {
        paused = true;
        stopAutoplay();
      });
      section.addEventListener('focusout', (event) => {
        if (section.contains(event.relatedTarget)) return;
        paused = false;
        startAutoplay();
      });

      if ('IntersectionObserver' in window) {
        const visibilityObserver = new IntersectionObserver((entries) => {
          inView = entries[0]?.isIntersecting === true;
          if (inView) startAutoplay();
          else stopAutoplay();
        }, { threshold: 0.2 });
        visibilityObserver.observe(section);
      }

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stopAutoplay();
        else startAutoplay();
      });

      let frame;
      let scrollEndTimer;
      const normaliseLoop = () => {
        const index = currentIndex();
        if (index < originalCount) return;
        jumpToIndex(index - originalCount);
        syncControls();
      };
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(syncControls);
        window.clearTimeout(scrollEndTimer);
        scrollEndTimer = window.setTimeout(normaliseLoop, 140);
      }, { passive: true });
      window.addEventListener('resize', () => {
        rebuildLoopClones(true);
        syncControls();
        syncExpanders();
        restartAutoplay();
      }, { passive: true });
      track.scrollLeft = 0;
      rebuildLoopClones(false);
      syncControls();
      requestAnimationFrame(syncExpanders);
      startAutoplay();
    });
  };


  const initialiseLifeRibbons = (root = document) => {
    root.querySelectorAll("[data-life-ribbon]:not([data-life-ribbon-ready])").forEach((section) => {
      const viewport = section.querySelector("[data-life-ribbon-viewport]");
      const track = section.querySelector("[data-life-ribbon-track]");
      if (!viewport || !track || track.children.length < 2) return;

      section.dataset.lifeRibbonReady = "true";
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      let paused = reducedMotion.matches;
      let visible = true;
      let offset = viewport.scrollLeft;
      let lastTime = 0;
      let frame = 0;
      let resumeTimer = 0;
      track.style.removeProperty("transform");

      const setPaused = (value) => {
        paused = value || reducedMotion.matches;
        lastTime = 0;
      };

      const stepWidth = () => {
        const first = track.firstElementChild;
        const second = first?.nextElementSibling;
        return first && second ? Math.max(1, second.offsetLeft - first.offsetLeft) : 1;
      };

      const tick = (time) => {
        if (!section.isConnected) return;
        if (!lastTime) lastTime = time;
        const delta = Math.min(50, time - lastTime);
        lastTime = time;

        if (paused) offset = viewport.scrollLeft;

        if (!paused && visible) {
          offset += delta * 0.018;
          viewport.scrollLeft = offset;
        }

        if (visible) {
          let step = stepWidth();
          while (offset >= step && track.firstElementChild) {
            offset -= step;
            track.append(track.firstElementChild);
            viewport.scrollLeft = offset;
            step = stepWidth();
          }
        }

        frame = window.requestAnimationFrame(tick);
      };

      const pauseForInteraction = () => {
        window.clearTimeout(resumeTimer);
        setPaused(true);
      };

      const resumeAfterInteraction = () => {
        window.clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(() => setPaused(false), 1200);
      };

      viewport.addEventListener("focusin", pauseForInteraction);
      viewport.addEventListener("focusout", resumeAfterInteraction);
      viewport.addEventListener("touchstart", pauseForInteraction, { passive: true });
      viewport.addEventListener("touchend", resumeAfterInteraction, { passive: true });
      viewport.addEventListener("touchcancel", resumeAfterInteraction, { passive: true });
      reducedMotion.addEventListener?.("change", () => setPaused(false));

      if ("IntersectionObserver" in window) {
        const observer = new IntersectionObserver(([entry]) => {
          visible = entry.isIntersecting;
          lastTime = 0;
        }, { threshold: 0.05 });
        observer.observe(section);
      }

      frame = window.requestAnimationFrame(tick);
      section.addEventListener("shopify:section:unload", () => window.cancelAnimationFrame(frame), { once: true });
    });
  };

  const initialiseGlobalTracking = () => {
    if (window.__nomHomeTrackingReady) return;
    window.__nomHomeTrackingReady = true;

    document.addEventListener('click', (event) => {
      const tracked = event.target.closest('[data-nom-event]');
      if (!tracked) return;
      if (tracked.dataset.nomEvent === 'route') {
        publish('nom_home_route_click', {
          route: tracked.dataset.route,
          location: tracked.dataset.location
        });
      }
      if (tracked.dataset.nomEvent === 'product') {
        publish('nom_home_product_click', {
          product_handle: tracked.dataset.productHandle || tracked.closest('[data-product-handle]')?.dataset.productHandle || '',
          section: tracked.dataset.section || ''
        });
      }
      if (tracked.dataset.nomEvent === 'standards') {
        publish('nom_home_standards_click', {
          destination: tracked.getAttribute('href') || '',
          link_label: tracked.textContent.trim()
        });
      }
    });

    document.addEventListener('toggle', (event) => {
      const disclosure = event.target.closest('[data-nom-faq]');
      if (!disclosure?.open) return;
      publish('nom_home_faq_open', { question_identifier: disclosure.dataset.nomFaq });
    }, true);
  };

  const initialiseAnnouncementFocus = () => {
    const slides = [...document.querySelectorAll('.top-bar__slide')];
    if (!slides.length) return;

    const syncSlide = (slide) => {
      const hidden = slide.getAttribute('aria-hidden') === 'true';
      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((control) => {
        if (hidden) {
          if (!control.hasAttribute('data-nom-original-tabindex')) {
            control.dataset.nomOriginalTabindex = control.getAttribute('tabindex') || '';
          }
          control.setAttribute('tabindex', '-1');
          return;
        }

        if (!control.hasAttribute('data-nom-original-tabindex')) return;
        const original = control.dataset.nomOriginalTabindex;
        if (original) control.setAttribute('tabindex', original);
        else control.removeAttribute('tabindex');
        delete control.dataset.nomOriginalTabindex;
      });
    };

    const observer = new MutationObserver((entries) => {
      entries.forEach((entry) => syncSlide(entry.target));
    });

    slides.forEach((slide) => {
      syncSlide(slide);
      observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] });
    });
  };

  const initialise = (root = document) => {
    initialiseReveals(root);
    initialiseReviewCarousels(root);
    initialiseLifeRibbons(root);
    const success = root.querySelector('[data-nom-elixir-form] .form-success');
    if (success && !success.dataset.nomEventSent) {
      success.dataset.nomEventSent = 'true';
      publish('nom_home_elixir_submit', { success: true });
    }
  };

  initialiseGlobalTracking();
  initialiseAnnouncementFocus();
  initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
